import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        // basis extensions — use the success/warning/info semantic tokens.
        success: "border-transparent bg-success text-success-foreground",
        warning: "border-transparent bg-warning text-warning-foreground",
        info: "border-transparent bg-info text-info-foreground",
        // SA-468: soft tones — a /10 wash with the strong ink, on a
        // transparent border. The register's chips, the Tasks chips and the
        // filter chips read from these, so "a shade darker" is one decision.
        neutral: "border-transparent bg-muted-foreground/10 text-foreground",
        "info-soft": "border-transparent bg-info/10 text-info",
        "warning-soft": "border-transparent bg-warning/10 text-warning-strong",
        "success-soft": "border-transparent bg-success/10 text-success",
        "destructive-soft": "border-transparent bg-destructive/10 text-destructive",
        "muted-soft": "border-transparent bg-muted text-muted-foreground",
        // No colour: the caller's className brings the wash (a queue state's accent).
        soft: "border-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  dot,
  children,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    /** SA-468: a background class for a leading 7px dot (ignored with asChild). */
    dot?: string;
  }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {dot && (
            <span aria-hidden="true" data-slot="badge-dot" className={cn("size-[7px] shrink-0 rounded-full", dot)} />
          )}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Badge, badgeVariants };
